import React from 'react';
import PropTypes from 'prop-types';
import { EVGrid, makeStyles } from '@eagleview/ev-comp-library';
import InputField from '../input-field';
import FormSelectField from '../form-select-field';
import PhoneInputField from '../phone-input-field';
import validateAddressInput from '../../utils/validateAddressInput.util';

const useStyles = makeStyles(() => ({
  state: {
    flex: 1,
  },
}));

const Address = (props) => {
  const classes = useStyles();
  const {
    inputValues, errorMessages, setInputValues, stateList, setErrorMessages, intputRefs, stateLabelText, inputId,
  } = props;

  const handleChange = (event) => {
    const {
      target: {
        id, value,
      },
    } = event;
    const idKey = id?.replace(inputId, '');
    const inputs = { ...inputValues };
    inputs[idKey] = value;
    setInputValues(inputs);
  };

  const handleInputBlur = (event) => {
    const { target: { id } } = event;
    const errors = { ...errorMessages };
    const idKey = id?.replace(inputId, '');
    errors[idKey] = validateAddressInput({ [idKey]: inputValues[idKey] }, inputValues.country)[idKey];
    setErrorMessages(errors);
  };

  const ids = {
    phone: `phone${inputId}`,
    streetAddress1: `streetAddress1${inputId}`,
    streetAddress2: `streetAddress2${inputId}`,
    city: `city${inputId}`,
    state: `state${inputId}`,
    zipCode: `zipCode${inputId}`,
  };

  return (
    <>
      <PhoneInputField
        labelText="Phone number"
        inputId={ids.phone}
        inputValue={inputValues.phone}
        variant="outlined"
        size="small"
        handleChange={handleChange}
        handleBlur={handleInputBlur}
        errorMessage={errorMessages.phone}
        isRequired
        ref={intputRefs.phone}
      />
      <InputField
        labelText="Address 1"
        inputId={ids.streetAddress1}
        inputValue={inputValues.streetAddress1}
        variant="outlined"
        size="small"
        handleChange={handleChange}
        handleBlur={handleInputBlur}
        errorMessage={errorMessages.streetAddress1}
        isRequired
        ref={intputRefs.streetAddress1}
      />
      <InputField
        labelText="Address 2"
        inputId={ids.streetAddress2}
        inputValue={inputValues.streetAddress2}
        variant="outlined"
        size="small"
        handleChange={handleChange}
        handleBlur={handleInputBlur}
        errorMessage={errorMessages.streetAddress2}
      />
      <EVGrid
        container
        style={{
          display: 'flex', alignItems: 'flex-start', gap: '16px', alignSelf: 'stretch',
        }}
      >
        <EVGrid item className={classes.state}>
          <InputField
            labelText="City"
            labelHeight={stateLabelText === 'State/ Province' ? '48px' : '24px'}
            inputId={ids.city}
            inputValue={inputValues.city}
            variant="outlined"
            size="small"
            handleChange={handleChange}
            handleBlur={handleInputBlur}
            errorMessage={errorMessages.city}
            isRequired
            ref={intputRefs.city}
          />
        </EVGrid>
        <EVGrid item style={{ width: '84px' }}>
          <FormSelectField
            labelText={stateLabelText}
            id={ids.state}
            selectValue={inputValues.state}
            selectList={stateList}
            handleSelectChange={handleChange}
            handleBlur={handleInputBlur}
            errorMessage={errorMessages.state}
            showDefaultOption={stateList.length === 0}
            defaultOption="State"
            isRequired
            ref={intputRefs.state}
          />
        </EVGrid>
        <EVGrid item style={{ width: '94px' }}>
          <InputField
            labelText="ZIP code"
            labelHeight={stateLabelText === 'State/ Province' ? '48px' : '24px'}
            inputId={ids.zipCode}
            inputValue={inputValues.zipCode}
            variant="outlined"
            size="small"
            handleChange={handleChange}
            handleBlur={handleInputBlur}
            errorMessage={errorMessages.zipCode}
            isRequired
            ref={intputRefs.zipCode}
          />
        </EVGrid>
      </EVGrid>
    </>
  );
};

Address.propTypes = {
  /**
   * @property {Function} setInputValues - Function to set input values
   */
  setInputValues: PropTypes.func.isRequired,
  /**
   * @property {array} selectList - Function to handle change in selected value in EVSelect
   */
  inputValues: PropTypes.shape().isRequired,
  /**
   * @property {array} stateList - Function to handle change in selected value in EVSelect
   */
  stateList: PropTypes.arrayOf(PropTypes.shape()).isRequired,
  /**
   * @property {shape} errorMessages - Error message to be displayed by helper text
   */
  errorMessages: PropTypes.shape().isRequired,
  /**
   * @property {Function} setErrorMessages - Function to set input errors
   */
  setErrorMessages: PropTypes.func.isRequired,
  /**
   * @property {shape} intputRefs - intputRefs
   */
  intputRefs: PropTypes.shape(),
  /**
   * @property {string} stateLabelText - stateLabelText
   */
  stateLabelText: PropTypes.string,
  /**
   * @property {string} inputId - id
   */
  inputId: PropTypes.string,
};

Address.defaultProps = {
  intputRefs: {},
  stateLabelText: 'Province',
  inputId: 'contact',
};

export default Address;
